import { Component } from '@angular/core';
import { NgFor } from '@angular/common';
import { RouterLink } from '@angular/router';

type Card = { title: string; icon: string; items: string[]; tone: 'a'|'b'|'c'|'d' };

@Component({
  selector: 'app-expertise',
  standalone: true,
  imports: [RouterLink, NgFor],
  template: `
  <section class="section" id="expertises">
    <div class="section__head">
      <h2 class="h2">Nos expertises</h2>
      <a class="link" routerLink="/services">Voir tous nos services →</a>
    </div>

    <div class="grid">
      <article class="card" *ngFor="let c of cards" [class]="c.tone">
        <div class="card__top">
          <span class="icon">{{c.icon}}</span>
          <h3 class="h3">{{c.title}}</h3>
        </div>

        <ul class="list">
          <li *ngFor="let it of c.items">{{it}}</li>
        </ul>
      </article>
    </div>
  </section>
  `,
  styles: [`
    /* Keep section container style, but make it responsive */
    .section{
      border-radius: 26px;
      border: 1px solid rgba(255,255,255,.10);
      background: rgba(255,255,255,.03);
      padding: 18px;
      box-shadow: 0 30px 120px rgba(0,0,0,.45);
    }

    .section__head{
      display:flex;
      align-items:flex-end;
      justify-content:space-between;
      gap:12px;
      margin-bottom: 14px;
      flex-wrap:wrap;
    }

    .link{ color: rgba(0,229,168,.92); font-weight: 900; }

    .grid{
      display:grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 12px;
    }

    .card{
      border-radius: 22px;
      border: 1px solid rgba(255,255,255,.10);
      background: rgba(255,255,255,.04);
      padding: 14px;
      position:relative;
      overflow:hidden;
      min-height: 180px;
      transition: transform .22s ease, box-shadow .22s ease, border-color .22s ease;
      animation: softFloat 7s ease-in-out infinite;
    }

    .card:hover{
      transform: translateY(-4px);
      box-shadow: 0 32px 90px rgba(0,0,0,.24);
      border-color: rgba(79,125,255,.24);
    }

    .card::before{
      content:"";
      position:absolute;
      inset:-60px;
      opacity:.65;
      pointer-events:none;
    }

    .a::before{ background: radial-gradient(circle at 20% 20%, rgba(79,125,255,.35), transparent 55%); }
    .b::before{ background: radial-gradient(circle at 20% 20%, rgba(255,193,7,.22), transparent 55%); }
    .c::before{ background: radial-gradient(circle at 20% 20%, rgba(168,85,247,.28), transparent 55%); }
    .d::before{ background: radial-gradient(circle at 20% 20%, rgba(0,229,168,.25), transparent 55%); }

    .card__top{ display:flex; align-items:center; gap:10px; position:relative; }

    .icon{
      width:40px; height:40px;
      display:grid; place-items:center;
      border-radius: 16px;
      border: 1px solid rgba(255,255,255,.12);
      background: rgba(255,255,255,.06);
      box-shadow: 0 16px 50px rgba(0,0,0,.35);
      position:relative;
      flex: 0 0 auto;
      animation: iconBob 4.8s ease-in-out infinite;
    }

    .h3{ margin:0; position:relative; }

    .list{
      margin: 12px 0 0;
      padding-left: 18px;
      color: rgba(235,240,255,.72);
      font-weight: 750;
      position:relative;
    }
    .list li{ margin: 6px 0; }

    @keyframes softFloat{
      0%,100%{ transform: translateY(0); }
      50%{ transform: translateY(-6px); }
    }

    @keyframes iconBob{
      0%,100%{ transform: translateY(0); }
      50%{ transform: translateY(-4px); }
    }

    :host-context(body[data-theme="light"]) .section{
      background: linear-gradient(180deg, rgba(255,255,255,.90), rgba(248,250,252,.86));
      border-color: rgba(15,23,42,.08);
      box-shadow: 0 24px 70px rgba(15,23,42,.08);
    }

    :host-context(body[data-theme="light"]) .card{
      background: linear-gradient(180deg, rgba(255,255,255,.96), rgba(248,250,252,.92));
      border-color: rgba(15,23,42,.08);
      box-shadow: 0 18px 40px rgba(15,23,42,.08);
    }

    :host-context(body[data-theme="light"]) .h3,
    :host-context(body[data-theme="light"]) .list{
      color: #0f172a;
    }

    :host-context(body[data-theme="light"]) .link{
      color: #10b981;
    }

    /* ✅ Tablet */
    @media (max-width: 960px){
      .section{ padding: 16px; border-radius: 22px; }
      .grid{ grid-template-columns: repeat(2, 1fr); }
      .card{ min-height: 170px; }
    }

    /* ✅ Phone (Hero-like density) */
    @media (max-width: 560px){
      .section{ padding: 14px; border-radius: 20px; }
      .section__head{ margin-bottom: 10px; }

      /* 2 columns on phone for this type of cards */
      .grid{ grid-template-columns: repeat(2, 1fr); gap: 10px; }

      .card{
        padding: 12px;
        border-radius: 18px;
        min-height: 150px;
      }
      .icon{ width: 36px; height: 36px; border-radius: 14px; }

      .list{ margin-top: 10px; padding-left: 16px; font-size: 12px; }
      .list li{ margin: 5px 0; }

      /* If you ever add longer text, prevent ugly overflow */
      .h3{ font-size: 16px; }
    }

    /* Extra-small phones: 1 column if needed */
    @media (max-width: 390px){
      .grid{ grid-template-columns: 1fr; }
    }
  `]
})
export class ExpertiseComponent {
  cards: Card[] = [
    {
      title: 'Data Analytics',
      icon: '📊',
      tone: 'a',
      items: ['KPI & reporting', 'Analyse exploratoire', 'Data storytelling'],
    },
    {
      title: 'BI & Viz',
      icon: '🧩',
      tone: 'b',
      items: ['Power BI, Qlik', 'Dashboards décisionnels', 'Gouvernance KPI'],
    },
    {
      title: 'Data Science & IA',
      icon: '🧠',
      tone: 'c',
      items: ['Forecasting', 'ML & scoring', 'Détection d’anomalies'],
    },
    {
      title: 'Data Engineering',
      icon: '⚙️',
      tone: 'd',
      items: ['ETL/ELT & pipelines', 'Data warehouse', 'Qualité & sécurité'],
    },
  ];
}
